import {
  defaultOfferDesign,
  type CoverageData,
  type FormConfig,
  type QuantityOfferConfig,
} from '@cod/config-schema';
import {
  buildCodButton,
  buildCodForm,
  codStyles,
  offersNode,
  type ProductContext,
} from '@cod/form-tree';
import { useEffect, useMemo, useState } from 'react';
import { ScaleToFit } from './ScaleToFit.js';
import { ensureSdkGlobals } from './sdkGlobals.js';
import { previewState } from './previewState.js';
import { PREVIEW_SURFACE_STYLE } from './previewSurface.js';
import { renderNubeTree } from './renderNubeTree.js';
import { getCoverage } from '../api/client.js';
import { useAppState } from '../state/AppState.js';

/**
 * Live preview. Builds the SHARED tree with `buildCodForm` and walks it to React,
 * so what the designer sees is pixel-identical to what buyers see on the storefront.
 *
 * ⚠️ No shadow root any more — see the wrapper's note. The admin's cascade reaches the preview,
 * which is the same exposure the storefront has to the merchant's theme.
 */
// Inline SVG thumbnail so the preview shows the product card without an external request.
const SAMPLE_IMAGE =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect width="64" height="64" fill="#dfe3ea"/><path d="M16 24h32v24H16z" fill="#b8c0cc"/><circle cx="26" cy="22" r="6" fill="#b8c0cc"/></svg>',
  );

/**
 * The placeholder product the preview falls back to. Its `currency`/`locale` are only ever used
 * when the store's own are unknown — see `context` below.
 */
const SAMPLE_CONTEXT: ProductContext = {
  storeId: 'preview',
  product: { id: 1, name: 'Camiseta de algodon', requires_shipping: true, image: SAMPLE_IMAGE },
  variant: { id: 1, price: 24999 },
  currency: 'ARS',
  locale: 'es-AR',
  quantity: 1,
};

// A representative offer for the Form Designer preview, so the "Quantity offers" field shows
// a real-looking block even before the merchant has created any offer (they may be styling
// the form first). The actual storefront renders the merchant's matching offer instead.
const SAMPLE_OFFER: QuantityOfferConfig = {
  id: 'preview-sample',
  name: 'Sample',
  enabled: true,
  products: [
    {
      id: SAMPLE_CONTEXT.product.id,
      name: SAMPLE_CONTEXT.product.name,
      variants: [],
      variant_options: [],
    },
  ],
  tiers: [
    {
      id: 'sample-1',
      title: 'Compra 1 unidad',
      quantity: 1,
      preselect: false,
      hide_compare_price: false,
      discount_type: 'none',
      discount_value: 0,
      price_color: '#000000',
      plaque_text: '',
      plaque_text_color: '#ffffff',
      plaque_bg_color: '#9aa0a6',
      image_url: '',
    },
    {
      id: 'sample-2',
      title: 'Compra 2 y ahorra 25%',
      quantity: 2,
      preselect: true,
      hide_compare_price: false,
      discount_type: 'percentage',
      discount_value: 25,
      price_color: '#000000',
      plaque_text: 'Ahorra 25%',
      plaque_text_color: '#ffffff',
      plaque_bg_color: '#0074bf',
      image_url: '',
    },
  ],
  design: defaultOfferDesign,
};

export function LivePreview({
  config,
  previewOffer,
  previewVariants,
  previewPrice,
  previewImage,
  offersOnly,
}: {
  config: FormConfig;
  /** Offer editor only: render its quantity-offer cards. */
  previewOffer?: QuantityOfferConfig;
  /** Offer editor only: the selected product's variants, so the preview's per-unit variant
   * selectors show real option names instead of nothing. */
  previewVariants?: Array<{ id: number; name: string }>;
  /** Offer editor only: the selected product's real unit price (major units), so the cards
   * show the actual product price instead of the placeholder sample price. */
  previewPrice?: number;
  /** Offer editor only: the selected product's thumbnail, so the cards show the real product
   * instead of the placeholder. It has to come in as the context's product image: the tier
   * thumbnail resolves the LIVE product before the offer's captured one, and here the live
   * product is the sample. */
  previewImage?: string;
  /** Offer editor only: render just the offer cards, not the full COD form. */
  offersOnly?: boolean;
}): JSX.Element {
  ensureSdkGlobals();
  const { settings } = useAppState();
  // Fetch the selected coverage's dataset so the preview shows real cascading dropdowns. Null
  // while loading or when no coverage is set — the form falls back to plain inputs.
  const coverageId = config.coverage?.id ?? null;
  const [coverageData, setCoverageData] = useState<CoverageData | null>(null);

  // The offer editor passes its own draft offer; the full Form Designer preview has none, so
  // fall back to the sample so the "Quantity offers" field has something to show.
  const offer = previewOffer ?? (offersOnly ? undefined : SAMPLE_OFFER);
  // Drive the variant selectors from the real selected product's variants when provided, and
  // the prices from the real product price (falling back to the sample only when unknown).
  //
  // 🚨 Money is formatted in the STORE's currency, not the sample's. The decimal places come
  // from the currency, so a hardcoded one does not merely mislabel the amount — it changes the
  // number: `Intl` gives COP, CLP and PYG no cents at all, so a 10.45 product previewed as ARS
  // reads "$ 10,45" while the storefront renders it "COP 10". The offer editor already feeds in
  // the merchant's REAL price, which is what made the mismatch visible.
  //
  // `store_language` is the BUYER-facing language (the admin's own `admin_language` is a
  // different setting — a Brazilian merchant may browse this in English), so it is the locale
  // the storefront formats in. Both fall back to the sample only when the install-time store
  // lookup never landed.
  const context: ProductContext = {
    ...SAMPLE_CONTEXT,
    product: {
      ...SAMPLE_CONTEXT.product,
      variants: previewVariants,
      image: previewImage ?? SAMPLE_CONTEXT.product.image,
    },
    variant: { ...SAMPLE_CONTEXT.variant, price: previewPrice ?? SAMPLE_CONTEXT.variant.price },
    currency: settings?.store_currency ?? SAMPLE_CONTEXT.currency,
    locale: settings?.store_language ?? SAMPLE_CONTEXT.locale,
  };

  useEffect(() => {
    let cancelled = false;
    if (!coverageId) {
      setCoverageData(null);
      return;
    }
    getCoverage(coverageId)
      .then((data) => {
        if (!cancelled) setCoverageData(data);
      })
      .catch(() => {
        if (!cancelled) setCoverageData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [coverageId]);

  // 🚨 No `mount`/`update`/`destroy` any more, and no shadow root.
  //
  // The preview is now a pure function of the same inputs the storefront gets: build the tree, walk
  // it to React, let React reconcile. That is what makes structural drift impossible rather than
  // merely unlikely — a block cannot exist here and not there, or render in a different order.
  //
  // ⚠️ Losing the shadow root means the admin's own styles now reach the preview. Every style
  // object in `codStyles()` is self-contained for exactly this reason (it had to be for the
  // storefront, which has no shadow root either), so the exposure is the same on both sides.
  const { node, css } = useMemo(() => {
    // `preview` mode passes no handlers, which is what keeps the preview inert.
    const build = {
      mode: 'preview' as const,
      state: previewState(offer),
      ...(offer ? { offer } : {}),
      ...(coverageData ? { coverage: coverageData } : {}),
    };
    if (offersOnly) {
      // The offer editor renders just the cards. Same builder, same styles — only the entry point
      // differs, so a card cannot look different here than inside the form.
      const cards = offer
        ? offersNode({ config: config, ctx: context, styles: codStyles(config), build }, offer, {
            aboveBuyButton: offer.design.display_above_buy_button,
          })
        : null;
      if (!cards) return { node: null, css: '' };
      // Above-the-button offers sit ON the product page, so the editor shows the (inert) COD
      // button beneath them — that is where the buyer sees the boundary between the two.
      return renderNubeTree(
        offer?.design.display_above_buy_button
          ? { type: 'col', children: [cards, buildCodButton(config)] }
          : cards,
      );
    }
    // 🚨 Popup mode shows the BUTTON above the open form, as the legacy preview did
    // (`form-renderer/index.ts:176`). The designer is configuring both, and the button carries its
    // own colours, icon and text — leaving it out hid half of what they were editing.
    const form = buildCodForm(config, context, build);
    return renderNubeTree(
      config.mode === 'popup' ? { type: 'col', children: [buildCodButton(config), form] } : form,
    );
    // `context` is rebuilt each render; key on its meaningful inputs instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, offer, offersOnly, previewVariants, previewPrice, previewImage, coverageData]);

  return (
    <ScaleToFit>
      <div
        style={{
          ...PREVIEW_SURFACE_STYLE,
          width: '100%',
          // 🚨 `min-width: 0` and `max-width: 100%`, because this div is very likely a flex item.
          // A flex item defaults to `min-width: auto`, so a percentage width inside it resolves
          // against the CONTENT and the whole preview grows to max-content — a horizontal scrollbar
          // that the storefront never shows.
          minWidth: 0,
          maxWidth: '100%',
          // The typeface is the one thing the preview CANNOT inherit correctly. `codStyles`
          // deliberately leaves `font-family` alone so the storefront picks up the merchant's theme
          // font and looks native (`THEME_RESET`'s note). The admin is not that theme, so without a
          // stack here the preview falls back to the browser default — a serif, against the
          // storefront's sans. This is the legacy stylesheet's own stack, which is what the Designer
          // showed before the migration.
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        }}
      >
        {/* The `styled()` rules — `::placeholder`, `:focus-within`, offer `@keyframes`. Rendered
            beside the tree because they are scoped to classes the walker generated for it. */}
        {css ? <style>{css}</style> : null}
        {node}
      </div>
    </ScaleToFit>
  );
}
